import React, { Fragment, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { CCard, CCardBody, CCardHeader, CCol, CRow, CSmartTable } from '@coreui/react-pro';

import { formatDate, getCountryLabelByCountry, getValueStateByCountry, scrollToTopOffset, tryParseJSON } from '../../../../../utils';
import { DEFAULT_DATE_MONTH_NAME_TIME_FORMAT, TYPE_QUESTION, USER_SURVEY_DETAIL_ADMIN_COLUMNS, SYSTEM_ADMIN_PATH } from '../../../../../constants';

import { fetchAdminUserSurveyDetailsRequest } from './action';
import { fetchAdminSurveyDetailsRequest } from '../Survey/actions';

const UserSurveyDetails = () => {
     const dispatch = useDispatch();
     const { id, userSurveyId } = useParams();

     const isLoading = useSelector((state) => state.general.isLoading);
     const survey = useSelector((state) => state.admin.surveyDetails);

     const [userSurveyDetails, setUserSurveyDetails] = useState();

     const sections = survey?.surveySections;

     const userResults = userSurveyDetails?.userSurveyResults[0];

     const fetchSurveyDetails = () => {
          if (!survey || survey?.id !== id) {
               dispatch(fetchAdminSurveyDetailsRequest(id));
          }
     };

     const fetchUserSurveyDetails = () => {
          dispatch(fetchAdminUserSurveyDetailsRequest(id, userSurveyId, setUserSurveyDetails));
     };

     useEffect(() => {
          scrollToTopOffset();

          fetchSurveyDetails();
          fetchUserSurveyDetails();
     }, []); // eslint-disable-line react-hooks/exhaustive-deps

     const AnswerResults = ({ item }) => {
          const answerResult = tryParseJSON(item.answer) || null;
          const likertScaleAnswerResult = answerResult?.likertScaleAnswer;

          const question = survey?.questions.find((ques) => ques.id === item.questionId);

          const questionType = question?.type;
          switch (questionType) {
               case TYPE_QUESTION.LIKERT_SCALE: {
                    if (!likertScaleAnswerResult) return <td>{item.answer}</td>;

                    return (
                         <td>
                              <ul className="ps-0" style={{ listStyle: 'none' }}>
                                   {likertScaleAnswerResult.map((answer, index) => {
                                        return (
                                             <li key={index}>
                                                  <strong>{answer.questionLikertName}: </strong>
                                                  <span>{answer.answerLikertName}</span>
                                             </li>
                                        );
                                   })}
                              </ul>
                         </td>
                    );
               }

               default:
                    return <td>{item.answer}</td>;
          }
     };

     return (
          survey && (
               <>
                    <div className="mb-2">
                         <Link className="mb-2" to={SYSTEM_ADMIN_PATH.SURVEY_USER_SURVEYS.replace(':id', id)}>{`< Go Back`}</Link>
                    </div>
                    <CCard className="mb-4">
                         <CCardHeader className="d-flex justify-content-between align-items-center">
                              <div className="card-header-text">
                                   <strong>User Survey Details</strong>
                              </div>
                         </CCardHeader>
                         <CCardBody>
                              <CRow>
                                   <CCol md="4">
                                        <p>
                                             <strong>First Name:</strong> {userSurveyDetails?.firstName}
                                        </p>
                                        <p>
                                             <strong>Last Name:</strong> {userSurveyDetails?.lastName}
                                        </p>
                                        <p>
                                             <strong>Email:</strong> {userSurveyDetails?.email}
                                        </p>
                                        {userSurveyDetails?.phone && (
                                             <p>
                                                  <strong>Phone:</strong> {userSurveyDetails.phone}
                                             </p>
                                        )}
                                   </CCol>
                                   <CCol md="4">
                                        <p>
                                             <strong>Date Submit:</strong>{' '}
                                             {formatDate({ date: userSurveyDetails?.createdAt, format: DEFAULT_DATE_MONTH_NAME_TIME_FORMAT })}
                                        </p>
                                        {userSurveyDetails?.timezone && (
                                             <p>
                                                  <strong>User Time Zone:</strong> {userSurveyDetails.timezone}
                                             </p>
                                        )}
                                   </CCol>
                                   <CCol md="12">
                                        {sections
                                             .sort((a, b) => a.order - b.order)
                                             .map((section) => {
                                                  const newUserResults = userResults?.filter((userResult) => {
                                                       const questionExists = section.questions.some((ques) => ques.id === userResult.questionId);
                                                       return questionExists;
                                                  });

                                                  const userResultData = newUserResults?.map((item) => {
                                                       let result = item;

                                                       if (item.type === TYPE_QUESTION.DROPDOWN_LIST_CHOICE) {
                                                            const countryQuestion = userResults.find((item) => item.answerType === 'country');

                                                            if (item.answerType === 'country') {
                                                                 result = { ...item, answer: getCountryLabelByCountry(item.answer) };
                                                            }

                                                            if (item.answerType === 'state' && typeof item.answer === 'string') {
                                                                 result = {
                                                                      ...item,
                                                                      answer:
                                                                           getValueStateByCountry(countryQuestion?.answer, item.answer) ||
                                                                           item.answer,
                                                                 };
                                                            } else if (item.answerType === 'state' && typeof item.answer === 'object') {
                                                                 result = {
                                                                      ...item,
                                                                      answer: item.answer.map((el, index) => {
                                                                           if (index === item.answer.length - 1) {
                                                                                return el.text;
                                                                           } else {
                                                                                return el.text + ', ';
                                                                           }
                                                                      }),
                                                                 };
                                                            }
                                                       }
                                                       return result;
                                                  });

                                                  return userResultData?.length > 0 ? (
                                                       <CCard className="mb-4" key={section.id}>
                                                            {section.title.trim() && (
                                                                 <CCardHeader className="d-flex justify-content-between align-items-center">
                                                                      <div className="card-header-text">
                                                                           <strong>{section.title}</strong>
                                                                           <div dangerouslySetInnerHTML={{ __html: section.description }} />
                                                                      </div>
                                                                 </CCardHeader>
                                                            )}
                                                            <CCardBody>
                                                                 <div className="table-responsive middle-table">
                                                                      <CSmartTable
                                                                           pagination
                                                                           columnSorter
                                                                           clickableRows
                                                                           itemsPerPage={20}
                                                                           itemsPerPageSelect
                                                                           loading={isLoading}
                                                                           items={userResultData}
                                                                           columns={USER_SURVEY_DETAIL_ADMIN_COLUMNS}
                                                                           scopedColumns={{
                                                                                answer: (item) => {
                                                                                     return <AnswerResults item={item} />;
                                                                                },
                                                                           }}
                                                                      />
                                                                 </div>
                                                            </CCardBody>
                                                       </CCard>
                                                  ) : (
                                                       <Fragment key={section.id}></Fragment>
                                                  );
                                             })}
                                   </CCol>
                              </CRow>
                         </CCardBody>
                    </CCard>
               </>
          )
     );
};

export default UserSurveyDetails;
